// Which language a staff screen reads in before anybody has signed in, and the
// browser's memory of the last one chosen. Only released languages are offered.
import { firstReleased, releasedFor } from './i18n/staffLanguage'
import { RELEASED_LANGUAGES } from './i18n/languages'
import type { StaffLanguage } from './staffText'

const KEY = 'qms.staffLanguage'

/** Before sign-in: this browser's last choice, else its own first released language, else English. */
export function signInLanguage(remembered: string | null, browser: readonly string[]): StaffLanguage {
  return firstReleased([remembered, ...browser], RELEASED_LANGUAGES)
}

/** The language last chosen on this browser, if released and storage is there to ask. */
export function rememberedLanguage(): StaffLanguage | null {
  try {
    const value = window.localStorage.getItem(KEY)
    return value && RELEASED_LANGUAGES.includes(value) ? value : null
  } catch {
    return null
  }
}

export function rememberLanguage(lang: StaffLanguage): void {
  try {
    window.localStorage.setItem(KEY, lang)
  } catch {
    // A private window, or storage switched off: the browser's own language is next.
  }
}

/**
 * The language to save to a person as they sign in, or null: only one pressed on
 * the sign-in screen, for a person with no language of their own, released for
 * them, and not what their browser would give them anyway.
 */
export function languageToKeep(
  pressed: StaffLanguage | null,
  signedIn: { staff: { language?: string | null }; releasedLanguages?: readonly string[] },
  browser: readonly string[],
): StaffLanguage | null {
  if (!pressed || signedIn.staff.language) return null
  const released = releasedFor(signedIn)
  if (!released.includes(pressed)) return null
  return pressed === firstReleased(browser, released) ? null : pressed
}
